import { pushToArray, queryAll, documentElement, sizeOfArray } from "@acng/frontend-bounty";
import { CTX_ALERT, CTX_OBSERVE, CTX_PROVIDE, CTX_REVOKE, CTX_SUBSCRIBE_CLASSNAME, CTX_SUBSCRIBE_TAGNAME, CTX_UNOBSERVE, CTX_VALUE, } from "../../minify.js";
/**
 * @template T
 * @typedef {import("./interface").Context<T>} Context
 */
/**
 * @template T
 * @typedef {import("./interface").ContextValue<T>} ContextValue
 */
/**
 * @template T
 * @typedef {Element & {
 *    [key: symbol]: ((value: T, previous?: T) => unknown) | undefined;
 * }} ConsumerElement
 */
/**
 * @template T
 * @param {T extends undefined ? never : T} value
 * @param {Array<string>} [sharedConsumerCollections]
 * @returns {Context<T>}
 */
export const createContextProvider = (value, sharedConsumerCollections) => {
    const providerSymbol = Symbol();
    const consumerCollections = sharedConsumerCollections ?? [];
    /**
     * @param {Element | undefined} provider
     * @param {ContextValue<T>} nextValue
     * @param {ContextValue<T>} previousValue
     */
    const provide = (provider, nextValue, previousValue) => {
        value = nextValue;
        if (sizeOfArray(consumerCollections)) {
            for (const element of /** @type {NodeListOf<ConsumerElement<T>>} */ (queryAll(consumerCollections.join(","), provider))) {
                element[providerSymbol]?.(value, previousValue);
            }
        }
        return nextValue;
    };
    return {
        [CTX_PROVIDE]: (providerElement, nextValue) => provide(providerElement ?? undefined, nextValue, value),
        [CTX_REVOKE]: () => { },
        [CTX_VALUE]: () => value,
        [CTX_SUBSCRIBE_TAGNAME]: (tagName) => pushToArray(consumerCollections, tagName),
        [CTX_SUBSCRIBE_CLASSNAME]: (names) => pushToArray(consumerCollections, names
            .split(" ")
            .map((s) => `.${s}`)
            .join("")),
        [CTX_OBSERVE]: (consumer, handler) => (( /** @type {ConsumerElement<T>} */(consumer)[providerSymbol] = handler), handler(value)),
        [CTX_ALERT]: (consumer, handler) => ( /** @type {ConsumerElement<T>} */(consumer)[providerSymbol] = (value) => {
            delete ( /** @type {ConsumerElement<T>} */(consumer)[providerSymbol]);
            handler(value);
        }),
        [CTX_UNOBSERVE]: (consumer) => delete ( /** @type {ConsumerElement<T>} */(consumer)[providerSymbol]),
    };
};
/**
 * Create an `Observable` to provide and observe values on DOM Elements.
 *
 * @template T
 * @param {T extends undefined ? never : T} value
 * The initial value must be defined.
 */
export function createGlobalContext(value) {
    return createContextProvider(value);
}
